import {
  Button,
  Card,
  CardContent,
  PageHeader,
  Skeleton,
} from "@roberttamas/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { CopyButton } from "@/components/copy-button";
import { Check, Download } from "@/components/icons";
import { SetupPromptIllustration } from "@/components/illustrations";
import { OrderStatusStrip } from "@/components/order-status-strip";
import { PaymentCard } from "@/components/payment-card";
import { PageSettingsCard } from "@/components/setup/page-settings-card";
import { STEP_EDIT_PAGES } from "@/components/setup/steps";
import { getMyOrder, getMyOrderInvoice, getMySetup } from "@/lib/api";
import { toAppError } from "@/lib/errors";
import type { GetMyOrderResp, GetMySetupResp } from "@/lib/types";

const POLL_MS = 30_000;

/**
 * The buyer's per-order dashboard. One page for the order's whole life:
 * QR payment while awaiting (polling for the paid flip — bank matching
 * runs on the invoice side, typically within an hour), then a "set up
 * your page" prompt, then the page's health strip + settings cards,
 * each editing on its own subroute (see MyOrderEditPage).
 */
export function MyOrderPage() {
  const { t } = useTranslation();
  const { orderId = "" } = useParams<{ orderId: string }>();
  const navigate = useNavigate();

  const orderQuery = useQuery<GetMyOrderResp>({
    queryKey: ["myOrder", orderId],
    queryFn: () => getMyOrder({ orderId }),
    enabled: orderId.length > 0,
    refetchInterval: (q) =>
      q.state.data?.status === "awaiting_payment" ? POLL_MS : false,
  });

  // getMySetup is server-gated to paid orders — never call it earlier.
  // Same query key the step components invalidate on save, so inline
  // edits refresh the dashboard automatically.
  const setupQuery = useQuery<GetMySetupResp>({
    queryKey: ["mySetup", orderId],
    queryFn: () => getMySetup({ orderId }),
    enabled: orderQuery.data?.status === "paid",
    retry: false,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const downloadInvoice = async () => {
    // Open the tab synchronously so the signed URL isn't popup-blocked.
    const tab = window.open("", "_blank");
    setDownloadingInvoice(true);
    try {
      const { url } = await getMyOrderInvoice({ orderId });
      if (tab) {
        tab.location.href = url;
      } else {
        window.location.href = url;
      }
    } catch (err) {
      tab?.close();
      toast.error(toAppError(err).message);
    } finally {
      setDownloadingInvoice(false);
    }
  };

  if (orderQuery.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-80 w-full" />
      </div>
    );
  }

  if (orderQuery.isError || !orderQuery.data) {
    return (
      <Card>
        <CardContent className="pt-6 text-sm text-muted-foreground">
          {t("myOrder.notFound")}
        </CardContent>
      </Card>
    );
  }

  const order = orderQuery.data;
  const { payment } = order;
  const setup = setupQuery.data;
  // Branch on the setup's site, not order.siteId: archived or reassigned
  // sites read as "no site" server-side.
  const site = setup?.site;

  const expired =
    order.status === "awaiting_payment" &&
    typeof payment?.dueDate === "string" &&
    new Date(payment.dueDate).getTime() < Date.now();

  return (
    <div className="space-y-6">
      <PageHeader
        backTo="/my-orders"
        backLabel={t("myOrder.backToMyOrders")}
        title={t("myOrder.title")}
        meta={
          <div className="flex items-center gap-1">
            <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
              {order.orderId}
            </code>
            <CopyButton value={order.orderId} label={t("myOrder.copyOrderNumber")} />
          </div>
        }
      />

      <OrderStatusStrip
        order={order}
        site={order.status === "paid" ? site : undefined}
      />

      {order.status === "awaiting_payment" && payment && (
        <>
          {expired && (
            <p className="rounded-md border border-dashed px-4 py-3 text-sm text-muted-foreground">
              {t("myOrder.expiredNote")}
            </p>
          )}
          <PaymentCard order={order} />
        </>
      )}

      {order.status === "awaiting_payment" && !payment && (
        <Card>
          <CardContent className="pt-6 text-sm text-muted-foreground">
            {t("myOrder.paymentUnavailable")}
          </CardContent>
        </Card>
      )}

      {order.status === "paid" && setupQuery.isLoading && (
        <Skeleton className="h-64 w-full" />
      )}

      {order.status === "paid" && setupQuery.isError && (
        <div className="space-y-3">
          <p className="text-sm text-destructive">
            {t("myOrder.settingsLoadFailed")}
          </p>
          <Button variant="outline" onClick={() => void setupQuery.refetch()}>
            {t("myOrder.tryAgain")}
          </Button>
        </div>
      )}

      {order.status === "paid" && setup && !site && (
        <>
          <Card>
            <CardContent className="flex flex-wrap items-start justify-between gap-4 pt-6">
              <div className="flex items-start gap-3">
                <Check className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
                <div className="text-sm">
                  <p className="font-medium">{t("myOrder.paymentReceived")}</p>
                  <p className="text-muted-foreground">
                    {t("myOrder.invoiceEmailed", { email: order.customer.email })}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                disabled={downloadingInvoice}
                onClick={() => void downloadInvoice()}
              >
                <Download className="h-4 w-4" />
                {downloadingInvoice ? t("myOrder.preparing") : t("myOrder.downloadInvoice")}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
              <SetupPromptIllustration className="h-60 w-60" />
              <Button asChild>
                <Link to={`/setup/${order.orderId}`}>{t("myOrder.setUpNow")}</Link>
              </Button>
            </CardContent>
          </Card>
        </>
      )}

      {order.status === "paid" && setup && site && (
        <PageSettingsCard
          setup={setup}
          onEdit={(step) =>
            navigate(`/my-orders/${orderId}/${STEP_EDIT_PAGES[step].segment}`)
          }
        />
      )}
    </div>
  );
}
